/** Чистая логика вкладки «Подбор поставки»: ключи товара и региона, поиск, ввод количества и
 *  дней, формула «до N дней». Дерево товар → регионы — draftTree.ts, цели «Куда планируем» —
 *  draftTargets.ts, черновик в localStorage — draftStorage.ts.
 *
 *  ⚠️ «Заполнить до N дней» — ВРЕМЕННОЕ согласованное исключение из правила «расчёты только в
 *  dwh» (решение владельца 19.10.2026): черновик qty = max(0, ⌈спрос FBO/день × N − остаток FBO⌉),
 *  где спрос FBO/день — `orders_fbo_28d` витрины (уже среднее в день за 28 дней, контракт v1.3),
 *  остаток — `fbo_stock`. «В пути» (запланированные / не принятые поставки) НЕ учтено. Регионы с
 *  NULL спросом или остатком не заполняются и считаются «пропущено». Результат — обычное
 *  редактируемое число. */
import type { WbOffer, WbOfferCell } from "@/api/wbRegions";
import { UNKNOWN } from "./state";

export const DEFAULT_FILL_DAYS = 30;
export const MAX_FILL_DAYS = 365;
export const MAX_QTY = 1_000_000;

export const FILL_FORMULA =
  "Черновик количества = max(0, ⌈спрос FBO/день (28 д) × N − остаток FBO⌉), вверх до целого — по каждому " +
  "ВКЛЮЧЁННОМУ региону («Куда планируем»). " +
  "В пути не учтено (запланированные и не принятые поставки не вычитаются). " +
  "Регионы без спроса или без остатка (нет данных) не заполняются, их количество очищается — счётчик «пропущено». " +
  "Если расчёт больше 1 000 000 — регион не заполняется, количество очищается («превышает лимит»). " +
  "Перезаписывает количество у ПОКАЗАННЫХ товаров, а если среди показанных есть отмеченные — только у них. " +
  "Временный расчёт в портале — согласованное исключение из правила «расчёты только в dwh».";

/** Ключ товара (оффера) черновика: кабинет + offer_key. */
export function productKey(legal: string, offerKey: string): string {
  return JSON.stringify([legal, offerKey]);
}

/** Ключ региона товара: кабинет + offer_key + регион (решение владельца 2; тот же, что у
 *  строки черновика v1 — количество переносится как есть). */
export function leafKey(legal: string, offerKey: string, regionWb: string | null): string {
  return JSON.stringify([legal, offerKey, regionWb]);
}

/** id региона для целей: имя региона WB, у «не определён» — `unknown`. */
export function regionId(c: WbOfferCell): string {
  return c.region_kind === "unknown" ? UNKNOWN : (c.region_wb ?? "");
}

/** Поиск как у матрицы (бэкенд `matches`): артикул продавца, nm, артикул и название 1С, название WB. */
export function matchesQuery(o: WbOffer, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return [o.vendor_code, o.mp_sku, o.article_1c, o.name_1c, o.name].some(
    (v) => v !== null && v !== undefined && String(v).toLowerCase().includes(needle),
  );
}

/** Количество из поля ввода: пусто — null, иначе целое 0…MAX_QTY; прочее — undefined (не принимать). */
export function parseQty(text: string): number | null | undefined {
  const t = text.trim();
  if (t === "") return null;
  if (!/^\d+$/.test(t)) return undefined;
  const n = Number(t);
  return n <= MAX_QTY ? n : undefined;
}

/** Дни «Заполнить до N»: целое 1…365; иначе null. */
export function parseDays(text: string): number | null {
  const t = text.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return n >= 1 && n <= MAX_FILL_DAYS ? n : null;
}

/** Черновик количества региона (см. шапку файла); null — спроса или остатка нет (NULL). */
export function fillQty(cell: WbOfferCell, days: number): number | null {
  const demand = cell.orders_fbo_28d;
  const stock = cell.fbo_stock;
  if (demand === null || stock === null) return null;
  // округление до 6 знаков до ⌈⌉: 0,1 × 30 в float — 3,0000000000000004, не 4
  const need = Math.round((demand * days - stock) * 1e6) / 1e6;
  return Math.max(0, Math.ceil(need));
}

// ─────────────── localStorage ───────────────

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** `localStorage` (доступ к нему сам может бросить — приватный режим, запрет сайта). */
export function browserStorage(): StorageLike {
  return localStorage;
}
